import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { TourOverlay } from "./tour-overlay";
import type { TourWithSteps } from "@shared/schema";

interface TourPlayerContextType {
  isPlaying: boolean;
  currentTourId: string | null;
  startTour: (tourId: string, restart?: boolean) => void;
  stopTour: () => void;
}

const TourPlayerContext = createContext<TourPlayerContextType | null>(null);

export function useTourPlayer() {
  const context = useContext(TourPlayerContext);
  if (!context) {
    throw new Error("useTourPlayer must be used within TourPlayerProvider");
  }
  return context;
}

export function TourPlayerProvider({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const { toast } = useToast();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTourId, setCurrentTourId] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedStepIds, setCompletedStepIds] = useState<string[]>([]);
  const isRestartingRef = useRef(false);
  const hasInitializedRef = useRef(false);

  const { data: tour } = useQuery<TourWithSteps>({
    queryKey: ["/api/tours", currentTourId],
    enabled: !!currentTourId && isPlaying,
  });

  const updateProgressMutation = useMutation({
    mutationFn: async (data: { tourId: string; currentStepId: string | null; completedSteps: string[]; status: string }) => {
      const response = await fetch(`/api/tours/${data.tourId}/progress`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to update progress");
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/tours", variables.tourId] });
      queryClient.invalidateQueries({ queryKey: ["/api/tours"] });
    },
  });

  useEffect(() => {
    if (!isPlaying || !tour || tour.steps.length === 0) return;
    const currentStep = tour.steps[currentStepIndex];
    if (!currentStep) return;
  }, [isPlaying, tour, currentStepIndex, location, setLocation]);

  const startTour = useCallback((tourId: string, restart = false) => {
    hasInitializedRef.current = false;
    isRestartingRef.current = restart;
    setCurrentTourId(tourId);
    setCurrentStepIndex(0);
    setCompletedStepIds([]);
    setIsPlaying(true);
  }, []);

  const stopTour = useCallback(() => {
    if (tour && currentTourId && currentStepIndex >= 0) {
      const currentStep = tour.steps[currentStepIndex];
      updateProgressMutation.mutate({
        tourId: currentTourId,
        currentStepId: currentStep?.id || null,
        completedSteps: completedStepIds,
        status: currentStepIndex > 0 || completedStepIds.length > 0 ? "in_progress" : "not_started",
      });
    }
    setIsPlaying(false);
    setCurrentTourId(null);
    setCurrentStepIndex(0);
    setCompletedStepIds([]);
    setLocation("/tours");
  }, [tour, currentTourId, currentStepIndex, completedStepIds, updateProgressMutation, setLocation]);

  useEffect(() => {
    if (!tour || !isPlaying || hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    // Navigate to the tour's target page
    if (tour.targetPage && location !== tour.targetPage) {
      setLocation(tour.targetPage);
    }

    if (isRestartingRef.current) {
      isRestartingRef.current = false;
      updateProgressMutation.mutate({
        tourId: tour.id,
        currentStepId: tour.steps[0]?.id || null,
        completedSteps: [],
        status: "in_progress",
      });
      return;
    }

    if (tour.progress?.currentStepId) {
      const stepIndex = tour.steps.findIndex(s => s.id === tour.progress?.currentStepId);
      if (stepIndex >= 0) setCurrentStepIndex(stepIndex);
    }
    if (tour.progress?.completedSteps) {
      setCompletedStepIds(Array.isArray(tour.progress.completedSteps) ? tour.progress.completedSteps as string[] : []);
    }
  }, [tour, isPlaying, updateProgressMutation, location, setLocation]);

  const handleNext = useCallback(() => {
    if (!tour) return;
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < tour.steps.length) {
      const currentStep = tour.steps[currentStepIndex];
      const updatedCompleted = [...completedStepIds];
      if (!updatedCompleted.includes(currentStep.id)) updatedCompleted.push(currentStep.id);
      setCompletedStepIds(updatedCompleted);
      setCurrentStepIndex(nextIndex);
      updateProgressMutation.mutate({
        tourId: tour.id,
        currentStepId: tour.steps[nextIndex].id,
        completedSteps: updatedCompleted,
        status: "in_progress",
      });
    }
  }, [tour, currentStepIndex, completedStepIds, updateProgressMutation]);

  const handlePrevious = useCallback(() => {
    if (!tour || currentStepIndex <= 0) return;
    const prevIndex = currentStepIndex - 1;
    setCurrentStepIndex(prevIndex);
    updateProgressMutation.mutate({
      tourId: tour.id,
      currentStepId: tour.steps[prevIndex].id,
      completedSteps: completedStepIds,
      status: "in_progress",
    });
  }, [tour, currentStepIndex, completedStepIds, updateProgressMutation]);

  const handleComplete = useCallback(() => {
    if (!tour) return;
    updateProgressMutation.mutate(
      { tourId: tour.id, currentStepId: tour.steps[tour.steps.length - 1].id, completedSteps: tour.steps.map(s => s.id), status: "completed" },
      {
        onSuccess: () => {
          toast({ title: "Tour completed!", description: `You've finished "${tour.title}"` });
          setIsPlaying(false);
          setCurrentTourId(null);
          setLocation("/tours");
        },
      }
    );
  }, [tour, updateProgressMutation, toast, setLocation]);

  return (
    <TourPlayerContext.Provider value={{ isPlaying, currentTourId, startTour, stopTour }}>
      {children}
      {isPlaying && tour && tour.steps.length > 0 && (
        <TourOverlay
          tourId={tour.id}
          tourTitle={tour.title}
          steps={tour.steps}
          currentStepIndex={currentStepIndex}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onClose={stopTour}
          onComplete={handleComplete}
        />
      )}
    </TourPlayerContext.Provider>
  );
}
